import { useRef, useEffect } from "react";
import { requestAudioPlay, releaseAudio, stopCurrentAudio } from "./audioManager";

/* ───────────── GLOBAL AUDIO LOCK ───────────── */

export function killAllAudio() {
  stopCurrentAudio();
}

/* ───────────── COMPONENT ───────────── */

export default function AudioPlayButton({
  src,
  value = {},
  onChange,
  mode = "edit",
  maxListens = 1
}) {
  const audioRef = useRef(null);

  const {
    listensUsed = 0,
    playing = false,
    currentTime = 0,
    exhausted = false // 🔑 NEW
  } = value;

  /* ───────── Sync STATE → AUDIO ───────── */

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      requestAudioPlay(audio);
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, [playing]);

  /* ───────── Cleanup ───────── */

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        releaseAudio(audioRef.current);
      }
    };
  }, []);

  /* ───────── Controls ───────── */

  function togglePlay() {
    if (mode !== "edit") return;
    if (exhausted) return; // 🚫 HARD STOP

    const audio = audioRef.current;
    if (!audio) return;

    // first and only allowed start
    if (!playing && audio.currentTime === 0) {
      if (listensUsed >= maxListens) return;

      onChange({
        ...value,
        listensUsed: listensUsed + 1,
        playing: true,
        currentTime: 0
      });
      return;
    }

    // pause / resume DURING the single listen
    onChange({
      ...value,
      playing: !playing,
      currentTime: audio.currentTime
    });
  }

  /* ───────── REAL AUDIO EVENTS ───────── */

  function handlePause() {
    // ignore pause caused by terminal end
    if (exhausted) return;

    if (playing) {
      onChange({
        ...value,
        playing: false,
        currentTime: audioRef.current?.currentTime ?? 0
      });
    }
    releaseAudio(audioRef.current);
  }

  function handleEnded() {
    // 🔒 terminal state
    onChange({
      ...value,
      playing: false,
      currentTime: 0,
      exhausted: true
    });
    releaseAudio(audioRef.current);
  }

  const disabled =
    mode !== "edit" ||
    exhausted;

  return (
    <div style={{ textAlign: "center", marginBottom: 12 }}>
      <audio
        ref={audioRef}
        src={src}
        preload="auto"
        onPause={handlePause}
        onEnded={handleEnded}
      />

      <button
        onClick={togglePlay}
        disabled={disabled}
        style={{
          padding: "10px 20px",
          fontSize: "16px",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.5 : 1
        }}
      >
        {playing ? "⏸ Pause" : "▶ Play"}
      </button>
    </div>
  );
}
